import { useEffect, useMemo, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { scrollRangeIntoView, searchItems } from '@/hooks/useVirtualListFind';
import { clearHighlights, highlightMatches } from '@/lib/highlight-matches';

import type { SummaryTimelineItem } from './summary-timeline.query';

const HIGHLIGHT_INSTANCE = 'summary-timeline';

export function entryElementId(uuid: string): string {
  return `summary-${uuid}`;
}

export function useSummaryFind(
  items: SummaryTimelineItem[],
  containerRef: React.RefObject<HTMLElement | null>,
  expand: (uuid: string) => void
) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [currentMatch, setCurrentMatch] = useState(0);

  const results = useMemo(
    () =>
      searchItems(
        { length: items.length, getText: (i) => items[i].text },
        query,
        caseSensitive
      ),
    [items, query, caseSensitive]
  );

  useEffect(() => setCurrentMatch(0), [results]);

  useEffect(() => () => clearHighlights(HIGHLIGHT_INSTANCE), []);

  useHotkeys(
    'mod+f',
    (e) => {
      e.preventDefault();
      setIsOpen(true);
    },
    { enableOnFormTags: true, enableOnContentEditable: true }
  );

  useEffect(() => {
    const result = results[currentMatch];
    if (!isOpen || !query || !result) {
      clearHighlights(HIGHLIGHT_INSTANCE);
      return;
    }
    const uuid = items[result.itemIndex].uuid;
    expand(uuid);
    const frame = requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        const container = containerRef.current;
        const element = document.getElementById(entryElementId(uuid));
        if (!container) return;
        const { currentRange } = highlightMatches(
          container,
          query,
          element,
          caseSensitive,
          result.occurrenceIndex,
          HIGHLIGHT_INSTANCE
        );
        if (currentRange && element) scrollRangeIntoView(currentRange, element);
        else element?.scrollIntoView({ block: 'center' });
      })
    );
    return () => cancelAnimationFrame(frame);
  }, [
    isOpen,
    query,
    caseSensitive,
    results,
    currentMatch,
    items,
    containerRef,
    expand,
  ]);

  const step = (delta: number) =>
    setCurrentMatch((m) =>
      results.length ? (m + delta + results.length) % results.length : 0
    );

  return {
    isOpen,
    query,
    setQuery,
    caseSensitive,
    setCaseSensitive,
    currentMatch,
    totalMatches: results.length,
    goToNext: () => step(1),
    goToPrevious: () => step(-1),
    close: () => {
      setIsOpen(false);
      setQuery('');
    },
  };
}
